import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { mockStats } from "../mock";
import { ArrowRight, Shield, Settings, Users, TrendingUp, Award, Clock, MapPin } from "lucide-react";

const AboutPage = () => {
  const { t } = useLanguage();

  const team = [
    {
      name: "Jean-Marc Dubois",
      role: "Président & Fondateur",
      experience: "25+ ans",
      speciality: "Ingénierie mécanique"
    },
    {
      name: "Marie-Claude Tremblay", 
      role: "Directrice technique",
      experience: "20+ ans",
      speciality: "Systèmes de levage"
    },
    {
      name: "Pierre Leblanc",
      role: "Chef de projet",
      experience: "15+ ans", 
      speciality: "Installation & maintenance"
    },
    {
      name: "Sophie Martin",
      role: "Ingénieure qualité",
      experience: "12+ ans",
      speciality: "Normes & certifications"
    }
  ];

  const certifications = [
    "ISO 9001:2015",
    "CSA B167-16",
    "ASME B30.2",
    "OSHA 1926.1400",
    "CWB Certified",
    "Transport Canada"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <Badge className="bg-excel-light-red text-white mb-6">
              {t("heroSlogan")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t("aboutTitle")}
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              {t("aboutDescription")}
            </p>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-4xl font-bold text-excel-red">
                {mockStats.yearsExperience}+
              </div>
              <div className="text-lg text-gray-600">{t("yearsExperience")}</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-excel-red">
                {mockStats.projectsDelivered}+
              </div>
              <div className="text-lg text-gray-600">{t("projectsDelivered")}</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-excel-red">
                {mockStats.complianceRate}%
              </div>
              <div className="text-lg text-gray-600">{t("complianceRate")}</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-excel-red">
                24/7
              </div>
              <div className="text-lg text-gray-600">Support technique</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <Badge className="bg-excel-light-red text-white">
                  Notre histoire
                </Badge>
                
                <h2 className="text-3xl md:text-4xl font-bold text-excel-dark">
                  Plus de 34 ans d'innovation
                </h2>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  Excel Levage, spécialiste québécois des équipements de levage, accompagne ses clients avec des solutions robustes et sécuritaires.
                </p>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  Nous nous engageons à fournir des solutions de levage industriel incluant la conception, la fabrication et la maintenance adaptées aux environnements manufacturiers et industriels.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-excel-light-red" />
                  <div>
                    <p className="font-medium text-excel-dark">Montréal, Québec</p>
                    <p className="text-sm text-gray-500">Siège social</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-excel-light-red" />
                  <div>
                    <p className="font-medium text-excel-dark">1999</p>
                    <p className="text-sm text-gray-500">Année de fondation</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_excel-revamp-quebec/artifacts/551kkd0h_7-%20A%20propos.png"
                alt="Excel Levage History"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              {t("ourValues")}
            </h2>
            <p className="text-xl text-gray-600">
              Les piliers qui guident notre excellence industrielle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center group hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-excel-red transition-colors">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">{t("safety")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">La sécurité est au cœur de chacune de nos créations. Conformité stricte aux normes CSA et ASME.</p>
              </CardContent>
            </Card>
            
            <Card className="text-center group hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-excel-red transition-colors">
                  <Settings className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">{t("reliability")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Équipements conçus pour durer avec des matériaux de qualité supérieure et une fabrication rigoureuse.</p>
              </CardContent>
            </Card>
            
            <Card className="text-center group hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-excel-red transition-colors">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">{t("performance")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Solutions optimisées pour maximiser votre productivité et réduire vos coûts opérationnels.</p>
              </CardContent>
            </Card>
            
            <Card className="text-center group hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
              <CardHeader>
                <div className="bg-excel-light-red w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-excel-red transition-colors">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-excel-dark">{t("customerService")}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Accompagnement personnalisé de la conception à la maintenance, avec une équipe d'experts dédiée.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              Notre équipe d'experts
            </h2>
            <p className="text-xl text-gray-600">
              Des professionnels passionnés au service de vos projets
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="text-center group hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-20 h-20 bg-excel-light-gray rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Users className="h-10 w-10 text-excel-steel" />
                  </div>
                  <CardTitle className="text-excel-dark">{member.name}</CardTitle>
                  <p className="text-excel-light-red font-medium">{member.role}</p>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <Clock className="h-4 w-4 text-excel-light-red" />
                    <span className="text-sm text-gray-600">{member.experience}</span>
                  </div>
                  <p className="text-sm text-gray-600">{member.speciality}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              Certifications & Conformité
            </h2>
            <p className="text-xl text-gray-600">
              Respect strict des normes industrielles les plus exigeantes
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="text-center group hover:shadow-lg transition-shadow border-l-4 border-l-excel-light-red">
                <CardHeader className="pb-4">
                  <div className="bg-excel-light-red w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <p className="font-semibold text-excel-dark text-sm">{cert}</p>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-excel-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 text-white">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Notre mission
              </h2>
              <p className="text-xl leading-relaxed">
                Fournir des solutions de levage industriel robustes, fiables et sécuritaires qui transforment les opérations de nos clients et contribuent à leur succès.
              </p>
              <p className="text-lg leading-relaxed text-gray-300">
                Nous nous engageons à concevoir, fabriquer et maintenir des équipements qui dépassent les attentes en matière de qualité, sécurité et performance.
              </p>
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Notre vision
              </h2>
              <p className="text-xl leading-relaxed">
                Être le leader reconnu au Québec pour les solutions de levage industriel innovantes et sur mesure.
              </p>
              <p className="text-lg leading-relaxed text-gray-300">
                Nous aspirons à développer continuellement notre expertise et nos technologies pour anticiper les besoins futurs de l'industrie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-excel-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à travailler avec nous ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Découvrez comment notre expertise peut transformer vos opérations de levage et de manutention.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-light-red hover:bg-excel-red text-white"
            >
              <Link to="/contact">
                Parler à un expert
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg"
              className="border-white text-white hover:bg-white hover:text-excel-dark"
            >
              <Link to="/case-studies">Voir nos réalisations</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;